import type { AuthContext, PermissionKey, PortalId } from "@livingsyncs/types";
import { AUDITOR_ROLE_KEYS } from "@livingsyncs/types";
import { RESOURCE_WRITE_READ_IMPLIES } from "./permissionImplies.generated";

/**
 * Permission rules shared by both apps (US-044 AC4).
 *
 * This module is the SINGLE source of truth for *what the rules are* — the
 * implication map and the `can`/`canAny`/`canAll` predicate — consumed
 * identically by the frontend (`lib/access.ts`) and the backend
 * (`hasPermission`). It does NOT unify enforcement: the backend stays the sole
 * authoritative, per-request, DB-backed decision point; the frontend uses these
 * rules only for UX. Deleting the entire frontend must leave the backend fully
 * secure (AC4d).
 *
 * Pure module — no server-only deps (same rule as @livingsyncs/types).
 */

/**
 * "manage" permissions imply their corresponding "view"/"report" permission:
 * if you can write, you can always read. Lifted verbatim from the previously
 * hand-mirrored copies in `frontend/hooks/useAuth.ts` and `backend/lib/auth.ts`
 * (byte-identical at the time of the lift — guarded by the backend parity test).
 */
export const PERMISSION_IMPLIES: Partial<
  Record<PermissionKey, readonly PermissionKey[]>
> = {
  // US-077 removed the legacy (audience-coupled) implication block — its parent
  // keys (staff:manage, calendar:manage, cases:manage, schedules:manage,
  // incidents:manage, residents:write) were retired in favour of the
  // resource-scoped catalog below.
  // US-049 — per-resource `write → read` for the 104-key catalog (generated;
  // ships dark). Elevated action keys are intentionally NOT implied by `:write`
  // (reclassification §3.2 — e.g. medication:write does NOT grant medication:administer).
  ...RESOURCE_WRITE_READ_IMPLIES,
};

/** Minimal context the predicate needs — just the resolved permission list. */
type PermissionCarrier = Pick<AuthContext, "permissions">;

/**
 * Does the context hold `key`, directly or via implication?
 * Holding a "manage" key satisfies a check for its paired "view" key.
 */
export function can(ctx: PermissionCarrier, key: PermissionKey): boolean {
  if (ctx.permissions.includes(key)) return true;
  return Object.entries(PERMISSION_IMPLIES).some(
    ([parent, implied]) =>
      implied.includes(key) && ctx.permissions.includes(parent),
  );
}

/** True if the context holds ANY of the keys (implication-aware). */
export function canAny(ctx: PermissionCarrier, keys: PermissionKey[]): boolean {
  return keys.some((key) => can(ctx, key));
}

/** True if the context holds ALL of the keys (implication-aware). */
export function canAll(ctx: PermissionCarrier, keys: PermissionKey[]): boolean {
  return keys.every((key) => can(ctx, key));
}

/**
 * The single positive source of truth for which portal a user belongs to
 * (US-044 AC3). Precedence is fixed and total: admin → audit → app → no-access.
 *
 * Imported by the frontend guards / `RoleBasedRedirect` AND the backend
 * `decide()` (US-045) — one function, no twin. A reimplemented copy would be a
 * new drift seam.
 *
 * NOTE: callers MUST resolve `useAuth` first — never call this on an undefined
 * context during load (deciding on `undefined` is the root cause of the /admin
 * 403 + FacilitySwitcher flash bugs; AC8/AC16).
 */
export function resolveHomePortal(ctx: AuthContext): PortalId | "no-access" {
  // Platform users (admin/manager) own the admin portal. US-077: the isPlatformAdmin /
  // isPlatformManager flags fully cover both platform roles (the prior platform:manage
  // permission check was redundant and the key is now removed).
  if (ctx.isPlatformAdmin || ctx.isPlatformManager) {
    return "admin";
  }

  // Auditors (group admin OR external) own the audit portal.
  if (ctx.isAuditorGroupAdmin || ctx.auditorGroupIds.length > 0) {
    return "audit";
  }

  // US-139: an auditor with NO group is a broken account, and the exact context
  // where the two auditor predicates disagree —
  //   isAuditor()       = isAuditorGroupAdmin || hasRole("role:audit:external")  → true
  //   resolveHomePortal = isAuditorGroupAdmin || auditorGroupIds.length > 0      → falls through
  //
  // Falling through reached the catch-all below, which admits anyone holding ANY
  // permission into the APP portal. Auditor roles carry audit:read/audit:write,
  // so a broken auditor account was handed an app-portal surface
  // (`/audit-management` is gated on audit:read) rather than being shut out.
  //
  // `auditorRoles.ts` blocks CREATING one, but cannot cover rows predating it or
  // an auditor group deleted later. Fail closed rather than silently downgrade
  // an auditor into a different audience.
  if (AUDITOR_ROLE_KEYS.some((key) => ctx.roles.includes(key))) {
    return "no-access";
  }

  // Catch-all heuristic — anyone authenticated with app-scope permissions or a
  // facility assignment is let INTO the app portal. This is NOT the
  // authorization itself: they are still bounced per-route by RouteGuard if
  // they lack the specific access permission.
  if (ctx.facilityIds.length > 0 || ctx.permissions.length > 0) {
    return "app";
  }

  // No permissions and no facility assignment — no meaningful access.
  return "no-access";
}
